import React, { memo, Fragment, useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { Table, Popconfirm, message, Input } from "antd";
import moment from "moment";

import Card from "../../../components/bootstrap/card";
import AdminEdit from "../app/admin-edit";
import ResetPassword from "../../modules/auth/pages/reset-password";

import { getUsers, deleteUser, updateUser } from "../../../services/UserService";

const { Search } = Input;

const resetPasswordIcon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M12 2C8.14 2 5 5.14 5 9C5 10.46 5.48 11.81 6.27 12.91L2.39 16.79C2.15 17.03 2 17.36 2 17.72V21C2 21.55 2.45 22 3 22H6.28C6.64 22 6.97 21.85 7.21 21.61L11.09 17.73C12.2 18.52 13.55 19 15 19C18.86 19 22 15.86 22 12C22 8.14 18.86 5 15 5C13.55 5 12.2 5.48 11.09 6.27L7.21 2.39C7.47 2.15 7.72 2 8 2H12ZM15 7V1L20 6L15 11V7Z" fill="currentColor"/>
</svg>
`;


const editIcon = ` <svg width="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11.4925 2.78906H7.75349C4.67849 2.78906 2.75049 4.96606 2.75049 8.04806V16.3621C2.75049 19.4441 4.66949 21.6211 7.75349 21.6211H16.5775C19.6625 21.6211 21.5815 19.4441 21.5815 16.3621V12.3341" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
<path fillRule="evenodd" clipRule="evenodd" d="M8.82812 10.921L16.3011 3.44799C17.2321 2.51799 18.7411 2.51799 19.6721 3.44799L20.8891 4.66499C21.8201 5.59599 21.8201 7.10599 20.8891 8.03599L13.3801 15.545C12.9731 15.952 12.4211 16.181 11.8451 16.181H8.09912L8.19312 12.401C8.20712 11.845 8.43412 11.315 8.82812 10.921Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
<path d="M15.1655 4.60254L19.7315 9.16854" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
</svg>`;

const deleteIcon = ` <svg width="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
<path d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
<path d="M20.708 6.23975H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
<path d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
</svg>`;

const SuperAdminList = memo(() => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [editUserData, setEditUserData] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false)

  /* ---------------- Fetch superadmins only ---------------- */
  const fetchSuperAdmins = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem("token");
      const res = await getUsers(token, 0);

      const allUsers = res?.data?.data?.users || [];
      const data = allUsers.filter(
        u => u.role === "superadmin"
      ); // Corrected closing parenthesis
      setUsers(data);
    } catch (err) {
      messageApi.error("Failed to fetch super admins");
    } finally {
        setLoading(false)
    }
  };

  useEffect(() => {
    fetchSuperAdmins();
  }, []);

  useEffect(() => {
    if (!searchText) {
      setFilteredUsers(users);
      return;
    }

    const lower = searchText.toLowerCase();
    const filtered = users.filter((u) => {
      // Robust search across all fields
      return Object.values(u).some(val =>
        val && String(val).toLowerCase().includes(lower)
      );
    });

    setFilteredUsers(filtered);
  }, [searchText, users]);
 

  /* ---------------- Delete ---------------- */
  const handleDelete = record => {
    const token = localStorage.getItem("token");
    const payload = {
      users: [
        {
          email: record.email,
          mobile: record.mobile,
          iec_no: record.iec_no
        }
      ]
    };
    deleteUser(token, payload).then(response => {
      if (response) {
        messageApi.success("User deleted");
        fetchSuperAdmins();
      }
    }, error => {
      console.log("error response: ", error.response)
      messageApi.error("Cannot delete user");
    })
  };

  /* ---------------- Edit ---------------- */
  const handleEdit = record => {
    const token = localStorage.getItem("token");
    updateUser(token, record).then(response => {
      if (response) {
        messageApi.success("Updated successfully");
        setShowEdit(false);
        fetchSuperAdmins();
      }}, error => {
      console.log("error response: ", error.response)
      messageApi.error("Cannot edit user");
    })
  };

  /* ---------------- Columns ---------------- */
  
  const columns = [
    {
  title: "Action",
  render: (_, record) => (
    <div className="flex align-items-center list-user-action">
      
      {/* EDIT */}
      <a
        onClick={() => {
          setEditUserData(record);
          setShowEdit(true);
        }}
        className="btn btn-sm btn-icon btn-warning"
        title="Edit"
      >
        <span
          className="btn-inner"
          dangerouslySetInnerHTML={{ __html: editIcon }}
        />
      </a>

      &nbsp;

      {/* DELETE */}
      <Popconfirm
        title="Delete Super Admin?"
        onConfirm={() => handleDelete(record)}
      >
        <a className="btn btn-sm btn-icon btn-danger" title="Delete">
          <span
            className="btn-inner"
            dangerouslySetInnerHTML={{ __html: deleteIcon }}
          />
        </a>
      </Popconfirm>

      &nbsp;

      {/* RESET PASSWORD */}
      <a onClick={() => {
          setEditUserData(record);
          setShowReset(true);
        }}
        className="btn btn-sm btn-icon btn-primary" title="Reset Password" >
        <span
          className="btn-inner"
          dangerouslySetInnerHTML={{ __html: resetPasswordIcon }}
        />
      </a>

    </div>
  )
},

    {
      title: "Name",
      dataIndex: "fullname"
    },
    {
      title: "Mobile",
      dataIndex: "mobile"
    },
    {
      title: "Email",
      dataIndex: "email"
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      render: d => moment(d).format("DD-MM-YYYY")
    }
  ];

  return (
    <Fragment>
      {contextHolder}

      {showEdit && (
        <AdminEdit
          show={showEdit}
          userData={editUserData}
          mode="superadmin-edit"
          handleClose={() => setShowEdit(false)}
          handleUpdate={handleEdit}
        />
      )}

      {showReset && (
        <ResetPassword
          show={showReset}
          userData={editUserData}
          handleClose={() => setShowReset(false)}
        />
      )}

      <Row>
        <Col sm="12">
          <Card>
            <Card.Header>
              <h4 className="card-title">Super Admin List</h4>
            </Card.Header>

            <div className="p-3">
              <Input
                placeholder="Search super admins..."
                allowClear
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 250, margin: 15 }}
              />
            </div>

            <Card.Body className="px-0">
              <Table
                columns={columns}
                dataSource={filteredUsers}
                rowKey="signal_users_id"
                scroll={{ x: 600 }}
                loading={loading}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
});

SuperAdminList.displayName = "SuperAdminList";
export default SuperAdminList;
